import { useCallback, useEffect, useState } from "react";
import BarChartIcon from "@mui/icons-material/BarChart";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import AssignmentLateIcon from "@mui/icons-material/AssignmentLate";
import AddchartIcon from "@mui/icons-material/Addchart";
import VisibilityIcon from "@mui/icons-material/Visibility";
import LowPriorityIcon from "@mui/icons-material/LowPriority";
import {
  buscaCategoriasTribPorTipoNegocio,
  buscarPreviasIcms,
  buscarPreviasIcmsPorTexto,
} from "../../services/api-planejamento-tributario.service";
import TableCard from "../../components/TableCard";
import { debounce } from "lodash";
import { formatValue } from "../../utils/formatValue";
import styles from "./styles.module.css";
import ModalEditar from "./ModalEditar";
import SnackBar from "../../components/SnackBar";
import SnackBarProgresso from "../../components/SnackBarProgresso";
import ModalVersoes from "./ModalVersoes";
import ModalVersoesProjeto from "../../components/Projeto/ModalVersoes";
import useClientPolling from "../../hooks/useClientPolling";
import { PreviaIcmsProvider, usePreviaIcms } from "./PreviaIcmsContext";
import ModalSelecaoEmpresa from "../../components/ModalSelecaoEmpresa";
import { criarNovaPreviaIcms } from "../../services/api-planejamento-tributario.service";

// Componente de Progresso - Usando SnackBarProgresso com Client Polling
const ProgressoComponent = () => {
  const {
    isConnected,
    progresso,
    erro,
    resultado,
    conectar,
    desconectar,
    limparEstado,
    criarVersaoComMotor,
    executarMotor,
  } = useClientPolling();

  // Mapear etapas para progresso de conclusão (atualizado com etapas do backend)
  const calcularProgresso = useCallback((etapa) => {
    const etapas = {
      validacao: 1,
      busca_grupo: 2,
      busca_empresas: 3,
      validacao_schemas: 4,
      criacao: 5,
      execucao_motores: 6,
      consolidacao: 7,
      concluido: 8,
      erro: 0,
    };
    return etapas[etapa] || 1;
  }, []);

  // Converter dados do Client Polling para o formato do SnackBarProgresso
  const [progressData, setProgressData] = useState(null);

  useEffect(() => {
    if (!progresso) {
      setProgressData(null);
      return;
    }

    // ✅ Mapeamento correto: dados vêm de progresso.status
    setProgressData({
      open: true,
      titulo:
        progresso.status?.mensagem || progresso.mensagem || "Processando...",
      etapaAtual: calcularProgresso(progresso.status?.etapa || progresso.etapa),
      totalEtapas: 8,
      progresso: progresso.status?.progresso || progresso.progresso || 0,

      // ✅ Informações detalhadas do backend
      empresaAtual: progresso.status?.empresaAtual,
      motorAtual: progresso.status?.motorAtual,
      empresasProcessadas: progresso.status?.empresasProcessadas,
      totalEmpresas: progresso.status?.totalEmpresas,
      motoresProcessados: progresso.status?.motoresProcessados,
      totalMotores: progresso.status?.totalMotores,

      erro: erro?.mensagem || null,
      sucesso: resultado ? true : false,
    });
  }, [progresso, erro, resultado, calcularProgresso]);

  const showProgress = isConnected || !!progresso || !!erro || !!resultado;

  useEffect(() => {
    return () => {
      // Cleanup ao desmontar
      if (isConnected) {
        desconectar();
      }
    };
  }, [isConnected, desconectar]);

  // Exposing methods for external use
  useEffect(() => {
    // Store methods in window for debugging if needed
    window.__sseDebug = {
      conectar,
      desconectar,
      limparEstado,
      criarVersaoComMotor,
      executarMotor,
      isConnected,
      progresso,
      erro,
      resultado,
    };
  }, [criarVersaoComMotor, executarMotor]);

  return (
    <>
      <SnackBarProgresso
        open={showProgress}
        onClose={() => {
          desconectar();
          limparEstado();
        }}
        progressData={progressData}
      />
    </>
  );
};

const columns = [
  "Razão Social",
  "CNPJ",
  "Categoria ",
  "Dt Inicial",
  "Dt Final",
  "Status",
];

const PreviaICMS = () => {
  const [previas, setPrevias] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalEditar, setModalEditar] = useState({ open: false, dados: {} });
  const [categorias, setCategorias] = useState([]);
  const [modalAnalisar, setModalAnalisar] = useState({});
  const [modalVersoes, setModalVersoes] = useState({
    open: false,
    idPrevia: null,
    isProjeto: false,
  });
  const [modalVersoesProjeto, setModalVersoesProjeto] = useState({
    open: false,
    idPrevia: null,
  });
  const [modalSelecaoEmpresa, setModalSelecaoEmpresa] = useState({
    open: false,
  });
  const [modalSolicitaDocs, setModalSolicitaDocs] = useState({
    open: false,
    idPrevia: null,
  });
  const [objSnackBar, setObjSnackBar] = useState({ type: "", msg: "" });

  const handleInititalData = useCallback(async () => {
    setLoading(true);
    try {
      const responsePrevias = await buscarPreviasIcms();
      const responseCategorias = await buscaCategoriasTribPorTipoNegocio(
        "ICMS"
      );
      const organizaCategoria = responseCategorias.categorias.map(
        (categoria) => {
          return {
            id: categoria.idCategoriaTributaria,
            label: categoria.descricao,
          };
        }
      );

      setCategorias(organizaCategoria);
      setPrevias(handleOrganizaDados(responsePrevias.previas));
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSnackBar = (obj) => {
    setObjSnackBar(obj);
  };

  const handleOrganizaDados = (dados) => {
    const btnAnalisar = (row) => {
      return {
        icon: <QueryStatsIcon />,
        title: "Analisar",
        onClick: () => handleModalAnalisar(row),
      };
    };
    const btnIniciarPrevia = (row) => {
      return {
        icon: <AddchartIcon />,
        title: "INICIAR PRÉVIA",
        onClick: () => handleModalEditar(true, row),
      };
    };
    const btnDocPendente = (row) => {
      return {
        icon: <AssignmentLateIcon />,
        title: "SOLICITAR DOCUMENTOS",
        onClick: () => handleModalSolicitaDocs(row.idPreviaIcms),
      };
    };
    const btnNovaVersao = (row) => {
      return {
        icon: <LowPriorityIcon />,
        title: "NOVA VERSÃO",
        onClick: () => handleClick(row.idPreviaIcms),
      };
    };
    const btnGerarProjeto = (row) => {
      return {
        icon: <AddchartIcon />,
        title: "GERAR PROJETO",
        onClick: () => {
          if (row.status !== "PREVIA-APROVADO") {
            handleSnackBar({
              type: "error",
              msg: "Prévia precisa estar APROVADA para gerar projeto.",
            });
            return;
          }
          setModalVersoesProjeto({ open: true, idPrevia: row.idPreviaIcms });
        },
      };
    };
    const btnVizualizar = {
      icon: <VisibilityIcon />,
      title: "Visualizar",
      onClick: () => handleClick(),
    };
    const formatStatus = (status) => {
      switch (status) {
        case "PREVIA-PENDENTE":
          return (
            <span className={styles.status_container}>
              <span className={styles.pendente} /> Pendente
            </span>
          );
        case "PREVIA-ANDAMENTO":
          return (
            <span className={styles.status_container}>
              <span className={styles.andamento} /> Andamento
            </span>
          );
        case "PREVIA-DOCUMENTACAO-PENDENTE":
          return (
            <span className={styles.status_container}>
              <span className={styles.documentacao} /> Documentação Pendente
            </span>
          );
        case "PREVIA-ENVIADA":
          return (
            <span className={styles.status_container}>
              <span className={styles.enviada} /> Enviada
            </span>
          );
        case "PREVIA-APROVADO":
          return (
            <span className={styles.status_container}>
              <span className={styles.aprovada} /> Aprovada
            </span>
          );
        case "PREVIA-CANCELADA":
          return (
            <span className={styles.status_container}>
              <span className={styles.cancelada} /> Cancelada
            </span>
          );
        case "PROJETO-INICIADO":
          return (
            <span className={styles.status_container}>
              <span className={styles.aprovada} /> Projeto Iniciado
            </span>
          );
        default:
          break;
      }
    };

    return dados.map((dado) => {
      const formatBtn = (status) => {
        switch (status) {
          case "PREVIA-PENDENTE":
            return [btnIniciarPrevia(dado), btnDocPendente(dado)];
          case "PREVIA-ANDAMENTO":
            return [btnAnalisar(dado)];
          case "PREVIA-APROVADO":
            return [btnGerarProjeto(dado), btnNovaVersao(dado)];
          default:
            return [btnVizualizar];
        }
      };
      return {
        razaoSocial: dado.razaoSocial,
        cnpj: formatValue("cnpj", dado.cnpj),
        categoria: dado.categoriaTributaria
          ? dado.categoriaTributaria.descricao
          : "-",
        dtInicial: formatValue("data", dado.dtInicial),
        dtFinal: formatValue("data", dado.dtFinal),
        status: formatStatus(dado.status),
        botoes: formatBtn(dado.status),
      };
    });
  };

  useEffect(() => {
    handleInititalData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Executar apenas na montagem do componente

  // ✅ NOVA FUNCIONALIDADE: Função global para simular clique no botão Analisar
  useEffect(() => {
    window.simularCliqueAnalisar = (
      idPrevia,
      tipoPrevia,
      isProjeto,
      dadosEmpresa
    ) => {
      // ✅ REABRIR MODAL DE VERSÕES COM LOADING
      setModalVersoes({
        open: true,
        idPrevia: idPrevia,
        isProjeto: isProjeto || false,
      });

      // ✅ ATUALIZAR DADOS DO MODAL ANALISAR SE NECESSÁRIO
      if (dadosEmpresa) {
        setModalAnalisar(dadosEmpresa);
      }
    };

    return () => {
      delete window.simularCliqueAnalisar;
    };
  }, []);

  const handleModalEditar = (open, dados) => {
    if (!open) {
      setModalEditar({ open, dados: {} });
    } else {
      setModalEditar({
        open: open ?? modalEditar.open,
        dados: dados ?? modalEditar.dados,
      });
    }
  };

  const handleClick = () => {};

  const handleModalSolicitaDocs = (idPrevia) => {
    setModalSolicitaDocs({ open: true, idPrevia });
  };

  const handleModalAnalisar = async (data) => {
    sessionStorage.setItem("cnpjSelecionado", data.cnpj);
    setModalVersoes({
      open: true,
      idPrevia: data.idPreviaIcms,
      isProjeto: false,
    });
    setModalAnalisar(data);
  };

  // Função para reabrir o modal após criar versão
  const handleReopenModalVersoes = () => {
    // Usar os dados da prévia atual (modalVersoes) em vez de modalAnalisar
    if (modalVersoes.idPrevia) {
      // Criar dados da prévia atual para reabrir o modal
      const dadosPreviaAtual = {
        idPreviaIcms: modalVersoes.idPrevia,
        razaoSocial: modalAnalisar?.razaoSocial || "Prévia Atual",
        categoria: modalAnalisar?.categoria || 0,
        status: modalAnalisar?.status || "PREVIA-ANDAMENTO",
        cnpj: modalAnalisar?.cnpj || "00000000000000",
        // Adicionar outros campos necessários se existirem
      };

      handleModalAnalisar(dadosPreviaAtual);
    } else {
      // Se não tiver dados suficientes, usar modalAnalisar como fallback
      if (modalAnalisar && Object.keys(modalAnalisar).length > 0) {
        handleModalAnalisar(modalAnalisar);
      }
    }
  };

  const handlePesquisaPorTexto = async (texto) => {
    setLoading(true);
    try {
      const responsePrevias = await buscarPreviasIcmsPorTexto(texto);
      setPrevias(handleOrganizaDados(responsePrevias.previas));
    } catch (error) {
      handleSnackBar({
        type: "error",
        msg: error.response?.data?.mensagem ?? "Ops, erro ao buscar prévias.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDebounce = debounce(handlePesquisaPorTexto, 300);

  const handleConfirmarSelecaoEmpresa = async (dadosSelecao) => {
    try {
      // Fechar modal imediatamente
      setModalSelecaoEmpresa({ open: false });
      setLoading(true);

      // Criar prévia com motor (UMA prévia para o grupo)
      const payload = {
        idGrupoEconomico: dadosSelecao.idGrupoEconomico,
        categoriaTributaria: dadosSelecao.idCategoria,
        tipoPrevia: "ICMS",
      };

      await criarNovaPreviaIcms(payload);

      // Mostrar mensagem de sucesso
      handleSnackBar({
        type: "success",
        msg: `Prévia criada com sucesso para o grupo ${dadosSelecao.nomeGrupo}`,
      });

      // Recarregar dados
      await handleInititalData();
    } catch (error) {
      handleSnackBar({
        type: "error",
        msg:
          error.response?.data?.mensagem ||
          error.message ||
          "Erro ao criar prévias. Tente novamente.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <PreviaIcmsProvider>
      <TableCard
        iconHeader={<BarChartIcon fontSize="1rem" />}
        titleHeader="Prévias ICMS"
        data={previas}
        setData={setPrevias}
        columns={columns}
        textHeaderCard="razaoSocial"
        handleDebounce={handleDebounce}
        loading={loading}
        temInsert={true}
        insertOpenModal={() => setModalSelecaoEmpresa({ open: true })}
      />

      <ModalEditar
        open={modalEditar.open}
        onClose={() => handleModalEditar(false)}
        dados={modalEditar.dados}
        categorias={categorias}
        rechargeData={handleInititalData}
        newPrevia
      />

      <ModalVersoes
        open={modalVersoes.open}
        onClose={() =>
          setModalVersoes({ open: false, idPrevia: null, isProjeto: false })
        }
        idPrevia={modalVersoes.idPrevia}
        tipoPrevia="ICMS"
        isProjeto={modalVersoes.isProjeto}
        contextHook={usePreviaIcms}
        dadosEmpresa={modalAnalisar}
        onReopenModal={() => {
          handleReopenModalVersoes();
        }}
      />

      <ModalVersoesProjeto
        open={modalVersoesProjeto.open}
        onClose={() => setModalVersoesProjeto({ open: false, idPrevia: null })}
        idPrevia={modalVersoesProjeto.idPrevia}
        tipoProjeto="ICMS"
        rechargeData={handleInititalData}
      />

      <ModalSelecaoEmpresa
        open={modalSelecaoEmpresa.open}
        onClose={() => setModalSelecaoEmpresa({ open: false })}
        onConfirm={handleConfirmarSelecaoEmpresa}
        tipoPrevia="ICMS"
      />

      {/* <ModalSolicitaDocs
        open={modalSolicitaDocs.open}
        setOpen={(value) =>
          setModalSolicitaDocs({ ...modalSolicitaDocs, open: value })
        }
        idPrevia={modalSolicitaDocs.idPrevia}
        product={"ICMS"}
      /> */}
      <SnackBar objSnackBar={objSnackBar} setObjSnackBar={handleSnackBar} />

      {/* Componente de Progresso */}
      <ProgressoComponent />
    </PreviaIcmsProvider>
  );
};

export default PreviaICMS;
